/* eslint-disable @typescript-eslint/no-explicit-any */
import { PAGE_LABELS } from "domain/configuration";
import { EligibilityType } from "domain/core/types";
import TagManager from "react-gtm-module";

export const toKVPairString = (obj: any, delimeter: string) =>
  Object.keys(obj)
    .filter((key) => !!obj[key])
    .map((key) => `${key}=${obj[key]}`)
    .join(delimeter);

interface AddressToFormat {
  number: string;
  street: string;
  suburb?: string;
  city?: string;
  postcode?: string;
  unit?: string;
  building_name?: string;
}
interface AnalyticsDataLayer {
  event?: string;
  plan_type?: string;
  icp_number?: string;
  address_found?: string;
  eligibility?: string;
  registry_load_type?: string;
  customer_type_system: string;
  self_select_load_group?: string;
  payment_success?: string;
  promo_code?: string;
  self_select_source?: string;
}

export const formatAddress = (address?: AddressToFormat) => {
  if (!address) return "";

  const { number, street, suburb, city, postcode, unit, building_name } =
    address;
  const formattedAddress: string[] = [
    `${building_name ? `${building_name} ` : ""}${
      unit ? `${unit}/` : ""
    }${number} ${street}`,
  ];
  if (suburb && suburb !== "") {
    formattedAddress.push(`, ${suburb}`);
  }
  if (city && city !== "") {
    formattedAddress.push(`, ${city}`);
  }
  if (postcode && postcode !== "") {
    formattedAddress.push(`, ${postcode}`);
  }

  return formattedAddress
    .join("")
    .toLowerCase()
    .replace(/\b[A-z]/g, (c) => c.toUpperCase());
};

export const trimNZMobileNumber = (number: string) =>
  number.replace(/(^\+640)|(^640)|(^64)|(^\+64)|(^0)|(\s)/g, "");

export const formatNumber = (storedNumber: string) =>
  `+64${trimNZMobileNumber(storedNumber)}`;

export const isValidNZNumber = (number: string) => {
  return trimNZMobileNumber(number).match(/^2([0-9]{7,9})$/g);
};

export const parseFlickError = (errorResponse?: {
  status?: number;
  data?: { errors?: string };
}) => {
  if (errorResponse?.status === 422) {
    if (errorResponse?.data?.errors?.includes("Promotional code")) {
      return "That promotional code does not exist";
    }
    if (errorResponse?.data?.errors?.includes("account number")) {
      return "Please double check your Direct Debit details above.";
    }
  }

  return "Something went wrong, please try again or click here to contact support.";
};

export const fetchPromoCookie = () => {
  const promoCookieRaw = document.cookie
    ?.split("; ")
    ?.find((row) => row.startsWith("promo"))
    ?.split("=")[1];

  if (promoCookieRaw === undefined) {
    return undefined;
  }

  return JSON.parse(promoCookieRaw);
};

export const fetchWindcaveTransactionId = () => {
  return localStorage.getItem("wctID");
};

export const sendAnalyticsPageEvent = (
  category: string,
  action: string | void,
  label: string | void,
) => {
  TagManager.dataLayer({
    dataLayer: {
      event: "page_event",
      category: category,
      action: action,
      label: label,
    },
  });
};

export const sendAnalyticsPageView = (page: string) => {
  TagManager.dataLayer({
    dataLayer: {
      event: "page_view",
      page: page,
      title: PAGE_LABELS[page],
    },
  });
};

export const sendAnalyticsCustomEvent = (
  eventName: string,
  eventData: AnalyticsDataLayer,
) => {
  eventData.event = eventName;
  TagManager.dataLayer({
    dataLayer: eventData,
  });
};

export const sendAnalyticsSignUpId = (id: string) => {
  TagManager.dataLayer({
    dataLayer: {
      event: "sign_up_complete",
      sign_up_id: id,
    },
  });
};

export const sendEligibility = (searchType: string, label: string) => {
  sendAnalyticsPageEvent("Bill estimate", searchType, label);
  sendAnalyticsPageEvent("Bill estimate", "Total address searches", label);
};

export const reportEligibility = (
  searchType: string,
  eligibility: EligibilityType | void,
) => {
  switch (eligibility) {
    case "success":
      sendEligibility(searchType, "Serviceable");
      break;
    case "manual":
      sendEligibility(searchType, "Serviceable");
      break;
    case "upgrade":
      sendEligibility(searchType, "Ineligible - upgrade");
      break;
    case "export":
      sendEligibility(searchType, "Ineligible - export");
      break;
    case "not_in_area":
      sendEligibility(searchType, "Ineligible - not-in-area");
      break;
    case "reject":
      sendEligibility(searchType, "Ineligible - reject");
      break;
  }
};

export const updateCurrentFormStep = (formStep: string) => {
  localStorage.setItem("currentFormStep", formStep);
};

export const MATCH_NZ_COUNTRY_CODE = "^([\\(+. ]*64[\\)-. ]*|[+ ]*|[+-. ]*)";
const NZ_MOBILE_NUMBER_PREFIX = `(02|2)`;
export const SANITIZED_MOBILE_NUMBER_LENGTH_CHECK = `([\\d][\\- ]*?){7,9}$`;

export const API_MOBILE_NUMBER_REGEX = `${MATCH_NZ_COUNTRY_CODE}${NZ_MOBILE_NUMBER_PREFIX}${SANITIZED_MOBILE_NUMBER_LENGTH_CHECK}`;

export const ALPHANUMERIC_PATTERN = "^[A-Za-z0-9\\s]+$";

// home number documentation: https://github.com/FlickElectric/how-to-big-success/blob/main/FAQ/Home-phone-number-formatting.md
export const HOME_NUMBER_REGEX = "^\\+?[1-9]\\d{1,14}$"; // this is a JS version of the ruby regex used on the API home number - E_DOT_164_REGEX = /\A\+?[1-9]\d{1,14}\z/

export const compareSanitizedStrings = (
  first: string | undefined,
  second: string | undefined,
) => {
  if (!first || !second) return false;

  return sanitizeString(first) === sanitizeString(second);
};

const sanitizeString = (string: string) =>
  string.toString().toLowerCase().split(/\s+/).join("");
